import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

// The token comes after "#" in the emailed link, so it never reaches a server's logs. It is read once and
// taken out of the address bar.
export function useLinkToken() {
  const [token] = useState(() => window.location.hash.slice(1));
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);
  return token;
}

export default function ResetPassword() {
  const token = useLinkToken();
  const [f, setF] = useState({ password: "", again: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.again) return toast.error("The two passwords differ.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: f.password });
      toast.success("Password changed. Sign in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) return (
    <AuthShell title="Reset link incomplete" subtitle="Open the link from the email again, or ask for a new one.">
      <Link to="/forgot-password" className="text-foreground underline text-sm">Ask for a new link</Link>
    </AuthShell>
  );

  return (
    <AuthShell title="Choose a new password" subtitle="At least 8 characters. You'll be signed out everywhere else.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="reset-password">New password</Label>
          <Input id="reset-password" type="password" autoComplete="new-password" minLength={8} required value={f.password}
            onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reset-password" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reset-again">Once more</Label>
          <Input id="reset-again" type="password" autoComplete="new-password" minLength={8} required value={f.again}
            onChange={(e) => setF({ ...f, again: e.target.value })} data-testid="reset-again" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Change password
        </Button>
      </form>
    </AuthShell>
  );
}
